import { cn, pageSizeMap } from "@reactive-resume/utils";
import { useCallback, useEffect, useRef, useState } from "react";

import { COACH_INTAKE_COLORS } from "./coach-theme";

/**
 * The résumé being analysed, with a scanning sweep over it.
 *
 * ⚠️ THIS SHOWS THE REAL DOCUMENT. It used to be twelve grey `div`s of random width — a mock of a
 * résumé. The screen's whole claim is "we are reading YOUR résumé against THIS job", and illustrating
 * that with a picture of a generic document undercuts it: the student has no way to tell the flow
 * picked up the right résumé until they land in the builder, which is exactly where discovering it
 * picked up the wrong one is most expensive.
 *
 * HOW IT RENDERS. `/artboard/preview` is the same non-interactive renderer the builder embeds
 * (see pages/builder/page.tsx) — `PreviewLayout`, not `BuilderLayout`, because the latter wraps
 * everything in zoom/pan controls that have no meaning inside a decorative card. The artboard is a
 * separate app with its own store, so the document is pushed in over `postMessage`.
 *
 * ⚠️ THE HANDSHAKE IS NOT OPTIONAL. `SET_RESUME` is fire-and-forget and never replayed, so a message
 * posted before the artboard's listener is live is dropped and the iframe stays blank forever. The
 * artboard posts `ARTBOARD_READY` once its listener is registered; that is the only reliable moment
 * to send. The iframe `load` event is not — React may schedule the artboard's effect after it. This
 * is the same bug, and the same fix, as the builder's preview.
 *
 * SCALING. The artboard renders a page at its true physical size (A4 = 210mm ≈ 794px). The card is a
 * few hundred pixels wide, so the iframe is rendered at natural size and CSS-scaled down. Scaling the
 * iframe rather than giving it the card's width matters: a narrow viewport would make the résumé's own
 * responsive layout reflow, and the student would be shown a document that looks nothing like the one
 * they are about to edit.
 */
export const CoachResumeScan = ({
  resumeData,
  scanning,
}: {
  /** The document to show. Null until the intake flow has resolved it, which is stage 2. */
  resumeData: unknown;
  /** False once the run has finished or failed — the sweep stops, the résumé stays. */
  scanning: boolean;
}) => {
  const [frame, setFrame] = useState<HTMLIFrameElement | null>(null);
  const [scale, setScale] = useState(0);

  const shellRef = useRef<HTMLDivElement>(null);

  // Held in a ref as well as a prop so the ARTBOARD_READY handler always posts the CURRENT document
  // without having to be torn down and re-registered every time the data changes — and so a READY
  // that arrives before the data is resolved can be answered later by the push effect below.
  const dataRef = useRef<unknown>(resumeData);
  dataRef.current = resumeData;

  const page =
    (resumeData as { metadata?: { page?: { format?: keyof typeof pageSizeMap } } } | null)?.metadata
      ?.page?.format ?? "a4";

  const naturalWidth = pageSizeMap[page].width * 3.78;
  const naturalHeight = pageSizeMap[page].height * 3.78;

  const push = useCallback(() => {
    if (!frame?.contentWindow || !dataRef.current) return;

    // Same-origin target rather than "*": the artboard is served by this app, and a résumé is the
    // student's own document — there is no reason for any other embedder to be able to read it.
    frame.contentWindow.postMessage(
      { type: "SET_RESUME", payload: dataRef.current },
      window.location.origin,
    );
  }, [frame]);

  // The authoritative trigger. Origin-checked, because this listener sits on the page and any frame
  // could post at it.
  useEffect(() => {
    const onMessage = (event: MessageEvent) => {
      if (event.origin !== window.location.origin) return;
      if ((event.data as { type?: string })?.type === "ARTBOARD_READY") push();
    };

    window.addEventListener("message", onMessage);
    return () => window.removeEventListener("message", onMessage);
  }, [push]);

  // Covers the other ordering: the artboard was already READY (message missed, or it fired while
  // `resumeData` was still null) and the document arrives afterwards.
  useEffect(push, [push, resumeData]);

  // Fit the page to whatever width the card ends up with. Observed rather than measured once,
  // because this card lives in a `lg:grid-cols-2` that collapses to one column — a layout change
  // with no re-render of its own would otherwise leave the résumé at the old scale.
  useEffect(() => {
    const shell = shellRef.current;
    if (!shell) return;

    const observer = new ResizeObserver(([entry]) => {
      const width = entry.contentRect.width;
      if (width > 0) setScale(width / naturalWidth);
    });

    observer.observe(shell);
    return () => observer.disconnect();
  }, [naturalWidth]);

  return (
    // TOP-ALIGNED, not centred. Both cards start their content at the same offset, so aligning the
    // paper's top edge with the checklist's first row is what makes the two columns read as one
    // panel. Centring it left the résumé floating halfway down a tall card with its top edge
    // level with nothing, which is what the screenshot showed.
    <div className="flex h-full items-start justify-center">
      {/* The "paper". Aspect-locked to the page so the card reserves its space before the iframe has
          painted — without it the layout jumps once the scale is measured. */}
      <div
        ref={shellRef}
        className="relative w-full max-w-[300px] overflow-hidden rounded-md bg-white shadow-[0_18px_50px_rgba(0,0,0,0.55)]"
        style={{ aspectRatio: `${naturalWidth} / ${naturalHeight}` }}
      >
        {resumeData ? (
          <iframe
            ref={setFrame}
            title="Resume preview"
            src="/artboard/preview"
            // No native scrollbars: this box shows a single page of the résumé at a fixed size, and
            // a longer document should be clipped by the shell's `overflow-hidden`, not grow its own
            // scrollbars inside the illustration. `overflow-hidden` on the iframe's style covers
            // browsers that ignore the legacy `scrolling` attribute.
            scrolling="no"
            // Pointer events off: this is an illustration. A student who clicks into it would be
            // interacting with a document that is about to be replaced by the optimized copy.
            className={cn(
              "pointer-events-none block overflow-hidden border-0 bg-white",
              scanning ? "animate-coach-scan-reveal" : "opacity-100",
            )}
            style={{
              width: `${naturalWidth}px`,
              height: `${naturalHeight}px`,
              transform: `scale(${scale})`,
              transformOrigin: "top left",
              overflow: "hidden",
              // Hidden until measured, so the full-size page is never shown for a frame.
              visibility: scale > 0 ? "visible" : "hidden",
            }}
            onLoad={push}
          />
        ) : (
          /* Stage 1 only — the document has not been resolved yet. Skeleton lines rather than a
             spinner, so the card's shape does not change when the real résumé replaces them. */
          <div className="space-y-2 p-5" aria-hidden>
            <div className="mx-auto h-2.5 w-1/2 rounded bg-zinc-200" />
            <div className="mx-auto h-1.5 w-2/3 rounded bg-zinc-100" />
            <div className="mt-5 space-y-1.5">
              {Array.from({ length: 16 }).map((_, index) => (
                <div
                  key={index}
                  className="h-1.5 rounded bg-zinc-100"
                  style={{ width: `${65 + ((index * 13) % 32)}%` }}
                />
              ))}
            </div>
          </div>
        )}

        {scanning && (
          // `inset-0` + `overflow-hidden` is what keeps the sweep INSIDE the paper. The line's
          // keyframes rebound between top 0% and 100% of this box, so it turns at the page edges
          // instead of running off them.
          <div className="pointer-events-none absolute inset-0 overflow-hidden" aria-hidden>
            {/* One travelling rail carrying both the bloom and the hairline, so they cannot drift
                apart. A zero-height box positioned by `top`; its children are centred on it with
                -50% translations, which is what makes the effect SYMMETRIC — and symmetry is the
                requirement here, because the line now travels up as well as down and a
                one-sided trailing gradient would lead the line on the return pass. */}
            <div className="animate-coach-scan-line absolute inset-x-0 h-0">
              {/* The bloom. Fades out equally above and below the line. */}
              <div
                className="animate-coach-scan-glow absolute inset-x-0 h-24 -translate-y-1/2"
                style={{
                  background:
                    `linear-gradient(to bottom, transparent, ${COACH_INTAKE_COLORS.scan}00 12%, ` +
                    `${COACH_INTAKE_COLORS.scan}40 50%, ${COACH_INTAKE_COLORS.scan}00 88%, transparent)`,
                }}
              />

              {/* The scanner head: a hairline with a tight halo. Two shadows rather than one blurred
                  div, which at this size reads as a smudge instead of a beam. */}
              <div
                className="absolute inset-x-0 h-px -translate-y-1/2"
                style={{
                  backgroundColor: COACH_INTAKE_COLORS.scan,
                  boxShadow:
                    `0 0 10px 1px ${COACH_INTAKE_COLORS.scan}, ` +
                    `0 0 24px 5px ${COACH_INTAKE_COLORS.scan}55`,
                }}
              />
            </div>
          </div>
        )}
      </div>
    </div>
  );
};
